import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook for handling async operations
 * @param {function} asyncFunction - Async function to execute
 * @param {boolean} immediate - Whether to execute the function immediately
 * @param {array} deps - Dependencies array for memoization
 * @returns {object} Async state and handlers
 */
const useAsync = (asyncFunction, immediate = false, deps = []) => {
  const [status, setStatus] = useState('idle');
  const [value, setValue] = useState(null);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);
  
  // Reset state on dependency changes
  useEffect(() => {
    setStatus('idle');
    setValue(null);
    setError(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  
  // Set isMounted to false when the component unmounts
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  // The execute function wraps asyncFunction and handles state changes
  const execute = useCallback((...args) => {
    setStatus('pending');
    setValue(null);
    setError(null);
    
    return asyncFunction(...args)
      .then(response => {
        if (isMounted.current) {
          setValue(response);
          setStatus('success');
        }
        return response;
      })
      .catch(error => {
        if (isMounted.current) {
          setError(error);
          setStatus('error');
        }
        return Promise.reject(error);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncFunction, ...deps]);
  
  // Execute the function immediately if requested
  useEffect(() => {
    if (immediate) {
      execute();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execute, immediate]);
  
  // Reset the state
  const reset = useCallback(() => {
    setStatus('idle');
    setValue(null);
    setError(null);
  }, []);
  
  return {
    execute,
    reset,
    status,
    value,
    error,
    isIdle: status === 'idle',
    isPending: status === 'pending',
    isSuccess: status === 'success',
    isError: status === 'error',
  };
};

/**
 * Custom hook for handling async operations with automatic retries
 * @param {function} asyncFunction - Async function to execute
 * @param {object} options - Configuration options
 * @returns {object} Async state and handlers
 */
export const useAsyncWithRetry = (
  asyncFunction,
  {
    immediate = false,
    maxRetries = 3,
    retryDelay = 1000,
    retryMultiplier = 2,
    deps = []
  } = {}
) => {
  const [retryCount, setRetryCount] = useState(0);
  const [retryTimer, setRetryTimer] = useState(null);
  const timeoutRef = useRef(null);
  
  const asyncState = useAsync(asyncFunction, false, deps);
  
  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  // Execute with retry logic
  const executeWithRetry = useCallback((...args) => {
    // Clear any existing retry timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    setRetryCount(0);
    setRetryTimer(null);
    
    const attempt = (currentRetry) => {
      return asyncState.execute(...args).catch(error => {
        if (currentRetry < maxRetries) {
          // Calculate exponential backoff delay
          const delay = retryDelay * Math.pow(retryMultiplier, currentRetry);
          
          setRetryCount(currentRetry + 1);
          setRetryTimer(delay);
          
          // Schedule retry
          return new Promise((resolve, reject) => {
            timeoutRef.current = setTimeout(() => {
              timeoutRef.current = null;
              setRetryTimer(null);
              
              attempt(currentRetry + 1).then(resolve).catch(reject);
            }, delay);
          });
        }
        
        // Max retries reached, propagate the error
        return Promise.reject(error);
      });
    };
    
    return attempt(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncState.execute, maxRetries, retryDelay, retryMultiplier]);
  
  // Execute immediately if requested
  useEffect(() => {
    if (immediate) {
      executeWithRetry();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executeWithRetry, immediate]);
  
  return {
    ...asyncState,
    execute: executeWithRetry,
    retryCount,
    retryTimer,
    isRetrying: retryTimer !== null,
  };
};

export default useAsync;
